import { h } from 'preact';
import { useMemo, useRef, useEffect, useState } from 'preact/hooks';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation, Zoom } from 'swiper';
import Media from 'utils/media';
import Image from 'utils/image';
import CloseIcon from 'utils/icon/icon-close.svg';
import ChevronRightIcon from 'utils/icon/icon-chevron-right.svg';
import ChevronDownIcon from 'utils/icon/icon-chevron-down.svg';

const ProductGalleryModal = ({
  media,
  initialIndex,
  lazyLoadDesktop,
  onClose,
  enableMute,
}) => {
  const [currentSlide, setCurrentSlide] = useState(null);

  const container = useRef(null);
  const thumbs = useRef(null);
  const backDrop = useRef(null);

  const handleClick = (index) => {
    container.current.scrollTo({
      top: container.current.childNodes[index].offsetTop + 1,
      left: 0,
      behavior: 'smooth',
    });
  };

  const observer = useMemo(
    () =>
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const { index } = entry.target.dataset;
          if (entry.intersectionRatio > 0) {
            thumbs.current?.childNodes[index].classList.add('active');
          } else {
            thumbs.current?.childNodes[index].classList.remove('active');
          }
        });
      }, {}),
    [],
  );

  useEffect(() => {
    if (container.current) {
      setTimeout(() => {
        container.current.childNodes[initialIndex].scrollIntoView({
          behavior: 'instant',
          block: 'start',
          inline: 'nearest',
        });
        backDrop.current.style.opacity = '0';
      }, 800);
      container.current.childNodes.forEach((element) =>
        observer.observe(element),
      );
    }

    return () => {
      observer.disconnect();
    };
  }, [observer, container]);

  return (
    <div className="fixed w-full h-full left-0 top-0 bg-white z-50">
      <div
        ref={backDrop}
        className="hidden tablet:block absolute z-50 inset-0 bg-grey-200 ease-out duration-300 pointer-events-none"
      ></div>
      <button
        onClick={onClose}
        className="right-9 tablet:right-40 tablet:top-9 p-16 top-0 absolute z-30"
      >
        <CloseIcon className="w-20 h-auto" />
      </button>

      <Swiper
        modules={[Pagination, Zoom, Navigation]}
        navigation={{
          prevEl: '.product-gallery-arrows .prev',
          nextEl: '.product-gallery-arrows .next',
          disabledClass: 'text-subdued !border-grey-400',
          clickable: true,
        }}
        initialSlide={initialIndex}
        className="h-full w-full absolute inset-0 tablet:invisible tablet:-z-10"
        zoom={true}
        loop={true}
        pagination={{
          type: 'fraction',
          horizontalClass:
            'heading-3 [&_*]:heading-3 pb-9 lc:pb-12 tablet:hidden',
        }}
        onSlideChange={(state) => {
          setCurrentSlide(state.activeIndex - 1);
        }}
      >
        {media.map((item, index) => (
          <SwiperSlide key={item.id}>
            <div
              className={
                item.mediaContentType === 'IMAGE'
                  ? 'swiper-zoom-container'
                  : '[&_.vimeo_div]:!static [&_.vimeo_div]:!p-0'
              }
            >
              <Media
                {...item}
                className="absolute inset-0 !object-cover h-full w-full"
                slideId={index}
                currentSlide={currentSlide}
                srcWidths={{
                  sm: 640,
                  md: 768,
                  lg: 1024,
                  xl: 1280,
                  '2xl': 1536,
                }}
                enableMute={enableMute}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="product-gallery-arrows tablet:hidden">
        <button className="z-10 prev absolute bottom-16 left-24 w-32 h-32 rounded-full lc:border border-grey-900 flex justify-center items-center">
          <ChevronRightIcon className="w-16 h-auto rotate-180 ml:hidden" />
          <ChevronDownIcon className="w-24 h-auto rotate-90 lc:hidden" />
        </button>
        <button className="z-10 next absolute bottom-16 right-24 w-32 h-32 rounded-full lc:border border-grey-900 flex justify-center items-center">
          <ChevronRightIcon className="w-16 h-auto ml:hidden" />
          <ChevronDownIcon className="w-24 h-auto -rotate-90 lc:hidden" />
        </button>
      </div>
      <div
        className="hidden tablet:!block z-20 absolute inset-0 h-full w-full overflow-y-auto [&_video]:static"
        ref={container}
      >
        {media.map((item, index) => (
          <div
            key={item.previewImage.id}
            className="relative -scroll-mt-1"
            style={{ zIndex: 100 - index }}
            data-index={index}
          >
            <Media
              {...item}
              className="w-full h-auto"
              useVideoRatio={true}
              lazyLoad={lazyLoadDesktop}
              forceLazyLoad={lazyLoadDesktop}
              srcWidths={{
                sm: 640,
                md: 768,
                lg: 1024,
                xl: 1280,
                '2xl': 1536,
              }}
              autoPlay={true}
              enableMute={enableMute}
              placeMuteTop={true}
            />
          </div>
        ))}
      </div>
      <div
        ref={thumbs}
        className="hidden tablet:!flex z-30 absolute h-full left-56 top-0  flex-col justify-center gap-8 overflow-hidden [&_.active+.active]:border-transparent"
      >
        {media.map((item, index) => (
          <div
            key={item.previewImage.id}
            className="[&.active]:border-black lc:border ml:border-b lc:rounded-4 border-transparent cursor-pointer"
            onClick={() => handleClick(index)}
          >
            {item.mediaContentType === 'EXTERNAL_VIDEO' ? (
              <img
                src={item.previewImage.src}
                className="object-cover w-50 h-[67px] lc:rounded-4"
              />
            ) : (
              <Image
                {...item.previewImage}
                classes="object-cover w-50 h-[67px] lc:rounded-4"
                srcWidths={{
                  sm: 100,
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductGalleryModal;
